import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { useGetDictionariesQuery } from '@/api/dictionaries/api';
import {
  useClearCellMutation,
  useClearFarmMutation,
  useGetFarmQuery,
  useLazyGetFacilityIndividualsQuery,
  useUpdateCellMutation,
  useUpdateFarmMutation,
} from '@/api/farming/api';
import { useGetOrganizationIdSetQuery } from '@/api/organizations/api';
import { addModalConfig } from '@/pages/Farming/components/FarmingCell/modal.config';
import { extraButtonConfig } from '@/pages/Farming/components/FarmingTable/buttons.config';
import { useColumns } from '@/pages/Farming/components/FarmingTable/useColumns';
import { UUID } from '@/shared/api/types';
import { hasPermission } from '@/shared/security/hasPermission';
import { farm } from '@/shared/security/permissions/farm';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { withSecurity } from '@/shared/security/withSecurity';

import { FarmFilterUpdate, TransformedTableData } from '../../types';
import cl from './styles.module.scss';

export interface canFarmAction {
  addToCell: boolean;
  clearCell: boolean;
  editRow: boolean;
  toIndivCard: boolean;
}

interface CurrentCell {
  id?: UUID;
  columnIndex: number;
  rowIndex: number;
}
type ConfirmModalType = 'CLEAR_CELL' | 'CLEAR_OBJECT' | 'DELETE_ROW';

export const FarmingTable = withSecurity(({ auth }) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [isModalAddOpen, setIsModalAddOpen] = useState<boolean>(false);

  const [currentCell, setCurrentCell] = useState<CurrentCell | null>();
  const [currentRowFilter, setCurrentRowFilter] = useState<FarmFilterUpdate | null>(null);

  const [confirmType, setConfirmType] = useState<ConfirmModalType | null>(null);

  const { indivLocation = '' } = useParams();
  const [searchParams] = useSearchParams();
  const farmId = searchParams.get('id') || '';
  const orgId = searchParams.get('orgId') || '';
  const facilityName = searchParams.get('facilityName') || '';
  const parentId = searchParams.get('parentId') || '';
  const activeCell = searchParams.get('activeCell') || '';

  const { data: orgData } = useGetOrganizationIdSetQuery([parentId], { skip: !parentId });

  const { data, isFetching } = useGetFarmQuery(
    { id: farmId, organizationId: orgId },
    { skip: !farmId || !orgId },
  );

  const [updateFarm, { isLoading: isUpdateFarm }] = useUpdateFarmMutation();
  const [clearFarm, { isLoading: isClearFarm }] = useClearFarmMutation();

  const [clearCell] = useClearCellMutation();
  const [updateCell] = useUpdateCellMutation();
  const [getIndividuals, { data: individuals }] = useLazyGetFacilityIndividualsQuery();
  const { data: dictionaries } = useGetDictionariesQuery();

  const tableData = (data as TransformedTableData) || {};
  const {
    columnsCount = 0,
    rowsCount = 0,
    transformedIndividualsTable = [],
    indivWithLocation,
  } = tableData;

  const canAction = useMemo(() => {
    return {
      addToCell: hasPermission(auth, { permissions: farm.addIndividualToCell }),
      clearCell: hasPermission(auth, { permissions: farm.freeCellFromIndividual }),
      editRow: hasPermission(auth, { permissions: farm.editScheme }),
      toIndivCard: hasPermission(auth, {
        permissions: vaccination.individual.viewPassport,
      }),
    };
  }, [auth]);

  const defineConfirmModal = (type: ConfirmModalType) => {
    setConfirmType(type);
    setIsConfirmOpen(true);
  };

  const handleFarmUpdate = useCallback(
    (filters: FarmFilterUpdate) => {
      return updateFarm({ filters, farmId });
    },
    [farmId, updateFarm],
  );

  const handleUpdateCell = useCallback(
    async ({ individualId }: Record<string, string>) => {
      if (individualId && currentCell) {
        await updateCell({
          id: individualId,
          farmId,
          columnIndex: currentCell.columnIndex,
          rowIndex: currentCell.rowIndex,
        });
        setIsModalAddOpen(false);
        setCurrentCell(null);
      }
    },
    [currentCell, farmId],
  );

  const handleAddIndividual = useCallback((columnIndex: number, rowIndex: number) => {
    setIsModalAddOpen(true);
    setCurrentCell({ columnIndex, rowIndex });
  }, []);

  const handleClearIndividual = useCallback(
    (id: UUID, columnIndex: number, rowIndex: number) => {
      defineConfirmModal('CLEAR_CELL');
      setCurrentCell({ id, columnIndex, rowIndex });
    },
    [],
  );

  const handleDeleteRow = useCallback((filters: FarmFilterUpdate) => {
    defineConfirmModal('DELETE_ROW');
    setCurrentRowFilter(filters);
  }, []);

  const columns = useColumns(
    columnsCount,
    handleDeleteRow,
    activeCell,
    handleAddIndividual,
    handleClearIndividual,
    canAction,
  );

  const sumbitClearCell = useCallback(() => {
    if (currentCell?.id) {
      clearCell({ cellId: currentCell?.id });
      setIsConfirmOpen(false);
      setCurrentCell(null);
    }
  }, [currentCell]);

  const submitClearObject = useCallback(async () => {
    try {
      await clearFarm({ farmId }).unwrap();
      setIsConfirmOpen(false);
    } catch (error) {
      console.error('Ошибка при очистке объекта:', error);
    }
  }, [farmId, clearFarm]);

  const submitDeleteRow = useCallback(() => {
    if (currentRowFilter) {
      updateFarm({ filters: currentRowFilter, farmId });
      setIsConfirmOpen(false);
      setCurrentRowFilter(null);
    }
  }, [currentRowFilter, updateFarm]);

  const typeConfirmModal = {
    CLEAR_CELL: {
      onSubmit: sumbitClearCell,
      text: 'Вы уверены, что хотите освободить ячейку?',
    },
    CLEAR_OBJECT: {
      onSubmit: submitClearObject,
      text: 'Вы уверены, что хотите очистить объект?',
    },
    DELETE_ROW: {
      onSubmit: submitDeleteRow,
      text: 'Вы уверены, что хотите удалить выбранные данные? При выборе варианта «Да» данные восстановлению не подлежат',
    },
  };

  useEffect(() => {
    if (farmId && indivLocation) {
      getIndividuals({
        structuralDivisionId: orgId,
        individualLocationId: indivLocation,
      });
    }
  }, [orgId && indivLocation]);

  return (
    <>
      {parentId && (
        <PageTitle level={4} title={`Хозяйство: ${orgData?.[0]?.name ?? ''}`} />
      )}
      <PageTitle
        className={cl.facilityName}
        title={
          facilityName ?? dictionaries?.dictMap.INDIVIDUAL_LOCATION?.[indivLocation]?.name
        }
      />
      <UTable
        size={'small'}
        data={transformedIndividualsTable || []}
        columns={columns}
        loading={isFetching}
        buttons={extraButtonConfig({
          disabledClearBtn: !columnsCount && !rowsCount,
          onFarmUpdate: handleFarmUpdate,
          onOpenModal: () => defineConfirmModal('CLEAR_OBJECT'),
          isLoading: isFetching || isUpdateFarm || isClearFarm,
        })}
        showTable={!!columnsCount}
      />
      <Modal
        mode={'form'}
        schema={addModalConfig(individuals || [], indivWithLocation ?? new Set())}
        title={'Добавить особь в ячейку'}
        open={isModalAddOpen}
        width="370px"
        onSubmit={handleUpdateCell}
        onCancel={() => {
          setIsModalAddOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'addForm'}
        destroyOnHidden={true}
      />
      <Modal
        mode={'confirm'}
        open={isConfirmOpen}
        onCancel={() => {
          setIsConfirmOpen(false);
        }}
        onSubmit={() => confirmType && typeConfirmModal[confirmType].onSubmit()}
        text={confirmType && typeConfirmModal[confirmType].text}
        submitText={'Да'}
        cancelText={'Нет'}
        destroyOnHidden={true}
      />
    </>
  );
});
